import React, { useState, useEffect, Fragment, useCallback, useRef } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  getFilteredRowModel,
  getPaginationRowModel,
  getFacetedUniqueValues
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowPathIcon, PencilIcon, TrashIcon, PlusCircleIcon, FunnelIcon, EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { initialpageSize } from '../Common/Contants';
import Popup from '../Common/Popup';
import ConfirmationPopup from '../Common/ConfirmationPopup';
import InputModal from '../Common/InputModal';
import axios from 'axios';
import { API_URL, PROJECT_URL, FUNCTION_API_URL } from '../../imports'
import { DebouncedInput, SelectTextFilterWithAllOption, StatusAdminFilter } from '../Common/Filters';
import FetchingDataLoader from '../Common/FetchingDataLoader';
import { handleApiError } from '../Common/APIUtils';
import PageSizeDropdown from '../Common/PageSizeDropdown';
import { formattedDate } from '../Common/CommonUtils';
import { Menu, Transition } from '@headlessui/react';
import SearchableSingleSelector from '../Common/SearchableSingleSelector';
import { Portal } from "react-portal";
import Pagination from '../Common/Pagination';
import MultiInputSelector from '../Common/MultiInputSelector';
import { jwtDecode } from 'jwt-decode';

// Helper function to transform backend data to frontend format
// const transformAssignmentData = (backendData, projects, users) => {
//   if (!backendData || !Array.isArray(backendData)) return [];
  
//   return backendData.map(assignment => {
//     // Find matching project
//     const project = projects.find(p => p.project_code === assignment.project_code);
    
//     // Find matching user
//     const user = users.find(u => u.user_id === assignment.user_id);
    
//     return {
//       id: assignment._id,
//       project_code: assignment.project_code,
//       project_name: project ? project.project_name : assignment.project_code,
//       user_id: assignment.user_id,
//       user_name: user ? user.name : assignment.user_id,
//       role: assignment.role,
//       status: assignment.status === 'active' ? true : false, // Convert string to boolean
//       creation_date: assignment.assigned_at,
//       creation_by: assignment.assigned_by,
//       updated_date: assignment.updated_at || null,
//       updated_by: assignment.updated_by || null
//     };
//   });
// };

// const transformAssignmentData = (backendData, projects, users) => {
//   if (!backendData || !Array.isArray(backendData)) return [];
  
//   return backendData.map(assignment => {
//     // Find matching project
//     const project = projects.find(p => p.project_code === assignment.project_code);
    
//     // Find matching user
//     const user = users.find(u => u.user_id === assignment.user_id);
    
//     return {
//       id: assignment._id,
//       project_code: assignment.project_code,
//       project_name: project ? project.name : assignment.project_code, // Changed from project.project_name to project.name
//       user_id: assignment.user_id,
//       user_name: user ? user.name : assignment.user_id,
//       role: assignment.role,
//       status: assignment.status === 'active' ? true : false,
//       creation_date: assignment.assigned_at,
//       creation_by: assignment.assigned_by,
//       updated_date: assignment.updated_at || null,
//       updated_by: assignment.updated_by || null
//     };
//   });
// };

// Simplified version - backend already provides everything
// const transformAssignmentData = (backendData) => {
//   if (!backendData || !Array.isArray(backendData)) return [];
  
//   return backendData.map(assignment => {
//     return {
//       id: assignment._id,
//       project_code: assignment.project_code,
//       project_name: assignment.project_name || assignment.project_code, // ✅ Already from backend
//       user_id: assignment.user_id,
//       user_name: assignment.user_name || assignment.user_email || assignment.user_id, // ✅ Already from backend
//       role: assignment.role,
//       status: assignment.status === 'active' ? true : false,
//       creation_date: assignment.assigned_at,
//       creation_by: assignment.assigned_by,
//       updated_date: assignment.updated_at || null,
//       updated_by: assignment.updated_by || null
//     };
//   });
// };

const transformAssignmentData = (backendData) => {
  if (!backendData || !Array.isArray(backendData)) return [];
  
  return backendData.map(assignment => {
    return {
      id: assignment._id,
      project_code: assignment.project_code,
      project_name: assignment.project_name || assignment.project_code,
      user_id: assignment.user_id, // This is MongoDB ObjectId
      user_name: assignment.user_name || assignment.user_email || assignment.user_id,
      role: assignment.role,
      status: assignment.status === 'active' ? true : false,
      creation_date: assignment.assigned_at,
      // creation_by: assignment.assigned_by,
      creation_by: assignment.assigned_by_name,
      assigned_by_name: assignment.assigned_by_name, 
      updated_date: assignment.updated_at || null,
      updated_by: assignment.updated_by || null
    };
  });
};

// Helper function to find assignment by ID (since you'll need it for update/delete)
// const findAssignmentById = (assignmentId) => {
//   return data.find(item => item.id === assignmentId);
// };

axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        // Add x-resource-group header to ALL requests
        config.headers['x-resource-group'] = decodedToken.rg_name || "IXO-MeghnadNext-rg-dev-01";
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default function ProjectAssignment() {
  console.log("🔴 ProjectAssignment component mounted");
  let [nonAdminUsers, setNonAdminUsers] = useState([]);
  let [projects, setProjects] = useState([]);

  let [selectedProject, setSelectedProject] = useState({});
  let [selectedUser, setSelectedUser] = useState({});
  const [unmatchedUsers, setUnmatchedUsers] = useState([]);

  let [isOpen, setIsOpen] = useState(false);
  let [rowData, setRowData] = useState([]);
  let [edit, setEdit] = useState(false);
  const [Loader, setLoader] = useState(true)
  let [isOpenPopup, setIsOpenPopup] = useState(false);
  let [msg, setMsg] = useState(false);
  let [isSuccess, setIsSuccess] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0 });
  const [activeDropdown, setActiveDropdown] = useState(null);
  const buttonRefs = useRef({});
  const dropdownRef = useRef(null);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateUserData = () => {
    const errors = {};
    
    if (!newUserData.name.trim()) errors.name = 'Name is required';
    if (!newUserData.email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newUserData.email)) errors.email = 'Invalid email';
    if (!newUserData.password) errors.password = 'Password is required';
    else if (newUserData.password.length < 8) errors.password = 'Password must be at least 8 characters';
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const calculatePosition = useCallback((id) => {
    const buttonRef = buttonRefs.current[id];
    if (buttonRef) {
        const rect = buttonRef.getBoundingClientRect();
        setDropdownPosition({
            top: rect.bottom + window.scrollY,
        });
        setActiveDropdown((prev) => (prev === id ? null : id));
    }
}, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    const handleKeyDown = (event) => {
      const keysToCloseDropdown = ['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp'];
      if (keysToCloseDropdown.includes(event.key)) {
        setActiveDropdown(null);
      }
    };
    const handleScroll = () => {
      setActiveDropdown(null);
    };
  
    if (activeDropdown !== null) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
      window.addEventListener("scroll", handleScroll, true);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [activeDropdown]);


  function closePopup() {
    setIsOpenPopup(false);
  }

  function openPopup() {
    setIsOpenPopup(true);
  }

  function closeModal() {
    setIsOpen(false);
    setRowData([]);
    setShowCreateUser(false);
    setNewUserData({ name: '', email: '', password: '' });
    setValidationErrors({});
  }

  // const openModal = useCallback(async (e, option) => {
  //   setRowData(e);
  //   if (option === 'edit') {
  //     setEdit(true);
  //     setSelectedProject(projects.filter(project => project.project_code === e.project_code)[0] || {});
  //     setSelectedUser(nonAdminUsers.filter(user => user.user_id === e.user_id)[0] || {});
  //   } else if (option === 'create') {
  //     setEdit(false);
  //     setSelectedProject({});
  //     setSelectedUser([]);
  //   }
  //   setIsOpen(true);
  // }, [projects, nonAdminUsers]);

  const openModal = useCallback(async (e, option) => {
    setRowData(e);
    // Clear new user form data when opening modal
    setNewUserData({ name: '', email: '', password: '' });
    setValidationErrors({});
    setShowCreateUser(false);

    if (option === 'edit') {
      setEdit(true);
      
      console.log("🔍 Edit - Row data:", e);
      console.log("🔍 Available projects:", projects);
      console.log("🔍 Available users:", nonAdminUsers);
      
      // ✅ Match project by project_code
      const matchedProject = projects.find(project => 
        project.project_code === e.project_code
      );
      
      console.log("🔍 Looking for project_code:", e.project_code);
      console.log("🔍 Found project:", matchedProject);
      setSelectedProject(matchedProject || {});
      
      // ✅ Match user - try multiple strategies
      // The user_id in assignment might be an ObjectId from MongoDB
      // We need to match it against user._id or user.email
      const matchedUser = nonAdminUsers.find(user => 
        user._id === e.user_id || // Match by MongoDB _id
        user.user_id === e.user_id || // Match by user_id (email)
        user.email === e.user_id // Match by email
      );
      
      console.log("🔍 Looking for user_id:", e.user_id);
      console.log("🔍 Found user:", matchedUser);
      
      // If no user found, show warning but still allow edit
      if (!matchedUser) {
        console.warn("⚠️ User not found in current users list. User may have been deleted.");
        setMsg("Warning: The assigned user no longer exists in the system. Please select a new user.");
        setIsSuccess(false);
        setIsOpenPopup(true);
      }
      
      setSelectedUser(matchedUser || {});
    } else if (option === 'create') {
      setEdit(false);
      setSelectedProject({});
      setSelectedUser([]);
    }
    setIsOpen(true);
  }, [projects, nonAdminUsers]);

  // const ReloadData = async (loadprops) => {
  //   setLoader(loadprops);
    
  //   try {
  //     // Fetch assignments
  //     // const assignmentsResponse = await axios.post(`${PROJECT_URL}/projects/list-users`, {
  //     //   headers: {
  //     //     Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  //     //   },
  //     //   data: {
  //     //     payload: {},
  //     //     userInfo: {
  //     //       _id: localStorage.getItem("user_id"),
  //     //       clientId: sessionStorage.getItem('client'),
  //     //       role: localStorage.getItem('role')
  //     //     }
  //     //   }
  //     // });
  //     const assignmentsResponse = await axios.post(
  //       `${PROJECT_URL}/projects/list-users`,
  //       {
  //         payload: {},
  //         userInfo: {
  //           _id: localStorage.getItem("user_id"),
  //           clientId: sessionStorage.getItem("client"),
  //           role: localStorage.getItem("role")
  //         }
  //       },
  //       {
  //         headers: {
  //           Authorization: `Bearer ${localStorage.getItem("access_token")}`
  //         }
  //       }
  //     );
      
  //     // Transform data to match table expectations
  //     const transformedData = transformAssignmentData(
  //       assignmentsResponse.data,
  //       projects,
  //       nonAdminUsers
  //     );
      
  //     setData(transformedData);
      
  //   } catch (error) {
  //     const errorMessage = handleApiError(error);
  //     setMsg(errorMessage);
  //     openPopup();
  //   } finally {
  //     setTimeout(() => {
  //       setLoader(false);
  //     }, 100);
  //   }

  //   // axios
  //   //   .get(`${API_URL}/projectusers`, {
  //   //     headers: {
  //   //       Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  //   //     },
  //   //     params: {
  //   //       client: sessionStorage.getItem('client')
  //   //     }
  //   //   })
  //   //   .then((response) => {
  //   //     setData(response.data.data);
  //   //   })
  //   //   .catch((error) => {
  //   //     const errorMessage = handleApiError(error);
  //   //     setMsg(errorMessage);
  //   //     openPopup();
  //   //   })
  //   //   .finally(() => {
  //   //     setTimeout(() => {
  //   //       setLoader(false);
  //   //     }, 100);
  //   //   });
  // };

  // const handleCreateState = async () => {
  //   if(selectedUser.length>0){
  //     setIsOpen(false);
  //     let selected_user_ids = selectedUser.map((item) => item.user_id);
  //     let create_form = { project_code: selectedProject.project_code, user_id: selected_user_ids };
  //     try {
  //       await axios
  //         .post(`${API_URL}/projectusers`, create_form, {
  //           headers: {
  //             Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  //           },
  //         })
  //         .then((response) => {
  //           setMsg(response.data.message);
  //           setIsSuccess(true);
  //           ReloadData(false);
  //           setIsOpenPopup(true);
  //         });
  //     } catch (error) {
  //       const errorMessage = handleApiError(error);
  //       setMsg(errorMessage);
  //       setIsSuccess(false);
  //       setIsOpenPopup(true);
  //     }
  //   }
  // };

  // const handleCreateState = async () => {
  
  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const decodedToken = jwtDecode(token);

      const payload = {
        payload: null,   // FIXED
        // payload: { project_code: null },
        userInfo: {
          _id: decodedToken.sub,
          role: decodedToken.role,
          clientId: decodedToken.client_id,
        },
      };

      const response = await axios.post(
        `${PROJECT_URL}/projects/list`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Filter only active projects
      const activeProjects = (response.data || []).filter(
        project => project.status === 'active' || project.status === 1 || project.status === '1'
      );

      return activeProjects;

      // return response.data || [];
    } catch (error) {
      console.error("Error fetching projects:", error);
      return [];
    }
  };

    // const fetchNonAdminUsers = async () => {
    //   try {
    //     const token = localStorage.getItem("access_token");
    //     const decodedToken = jwtDecode(token);

    //     // const payload = {
    //     //   payload: {
    //     //     // project_code: selectedProject.project_code
    //     //     project_code: null
    //     //   },
    //     //   userInfo: {
    //     //     _id: decodedToken.sub,
    //     //     role: decodedToken.role,
    //     //     clientId: decodedToken.client_id,
    //     //   },
    //     // };

    //     // ✅ Use correct endpoint that returns ALL users for the client
    //     // const response = await axios.post(
    //     //   `${PROJECT_URL}/projects/list-users`,
    //     const response = await axios.get(
    //       `${FUNCTION_API_URL}/users`,
        
    //       // payload,
    //       {
    //         headers: {
    //           Authorization: `Bearer ${token}`,
    //           'Cache-Control': 'no-cache',
    //           'Pragma': 'no-cache'
    //         },
    //         params: {
    //           clientId: decodedToken.client_id
    //         }
    //       }
    //     );

    //     // const users = response.data || [];
        
    //     // console.log("✅ Users loaded:", users);
        
    //     // // ✅ Format users correctly
    //     // const formattedUsers = users.map(user => ({
    //     //   _id: user._id,              // Keep MongoDB ObjectId for matching
    //     //   user_id: user.email,         // Use email as user_id
    //     //   name: user.name,             // Display name
    //     //   email: user.email,           // Keep email for assignment
    //     //   role: user.role              // Keep role
    //     // }));

    //     const users = response.data || [];
    
    //     // ✅ Additional client filtering if backend doesn't handle it
    //     // const currentClientId = sessionStorage.getItem('client');
    //     // const filteredUsers = users.filter(user => 
    //     //   user.client_id === currentClientId || 
    //     //   user.clientId === currentClientId
    //     // );
        
    //     // console.log("✅ Client-filtered users:", filteredUsers);
        
    //     // const formattedUsers = users.map(user => ({
    //     //   _id: user._id,
    //     //   user_id: user.email,
    //     //   name: user.name,
    //     //   email: user.email,
    //     //   role: user.role
    //     // }));

    //     // Remove duplicates by user_id
    //     // const uniqueUsers = users.reduce((acc, user) => {
    //     //   if (!acc.find(u => u.user_id === user.user_id)) {
    //     //     acc.push(user);
    //     //   }
    //     //   return acc;
    //     // }, []);

    //     const formattedUsers = users
    //       .filter(user => user.status === 'active' || user.status === true) 
    //       .map(user => ({
    //         _id: user._id,
    //         user_id: user.user_id,          // correct
    //         name: user.user_name,           // correct
    //         email: user.user_email,         // correct
    //         role: user.role
    //     }));
        
    //     console.log("✅ Formatted users:", formattedUsers);
        
    //     return formattedUsers;

    //   } catch (error) {
    //     console.error("❌ Error fetching users:", error);
    //     return [];
    //   }
    // };

    const fetchNonAdminUsers = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const decodedToken = jwtDecode(token);

        const response = await axios.get(
          `${FUNCTION_API_URL}/users`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: {
              clientId: decodedToken.client_id
            }
          }
        );

        console.log("Raw response:", response.data);
        
        // Handle different response structures
        let users = [];
        if (Array.isArray(response.data)) {
          users = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          users = response.data.data;
        } else {
          console.error("Unexpected response structure:", response.data);
          return [];
        }
        
        const formattedUsers = users
          .filter(user => 
            (user.status === true || user.status === 'active') && 
            user.role_code !== 'ProjectAdmin' && 
            user.role_code !== 'SuperAdmin'
          )
          .map(user => ({
            _id: user.id,
            user_id: user.user_id,
            name: user.name,
            email: user.email,
            role: user.role_code
          }));
        
        console.log("✅ Formatted active users:", formattedUsers);
        
        return formattedUsers;

      } catch (error) {
        console.error("❌ Error fetching users:", error);
        return [];
      }
    };

    const fetchInitialData = async () => {
      console.log("🔵 fetchInitialData START");
      try {
        const token = localStorage.getItem("access_token");
        const decodedToken = jwtDecode(token);
        const response = await axios.post(
          `${PROJECT_URL}/projects/list-users`,
          {
            // payload: null,  // FIXED
            payload: { project_code: null },
            userInfo: {
              _id: sessionStorage.getItem("userid"),   // FIXED — MUST use sessionStorage
              // clientId: sessionStorage.getItem("client"),
              clientId: decodedToken.client_id,
              // role: sessionStorage.getItem("role"),
              role: decodedToken.role,
            }
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            }
          }
        );
        console.log("🔵 fetchInitialData response:", response.data);
        return response.data || [];
      } catch (error) {
        console.error("❌ fetchInitialData error:", error);
        const errorMessage = handleApiError(error);
        setMsg(errorMessage);
        openPopup();
        return [];
      }
    };

    // const ReloadData = async (loadprops) => {
    //   setLoader(loadprops);
      
    //   try {
    //     // Add a small delay to ensure backend has processed the new user
    //     if (loadprops) {
    //       await new Promise(resolve => setTimeout(resolve, 1500));
    //     }
    //     // Reload all data to get updated assignments and users
    //     const [projectsData, usersData, assignmentsData] = await Promise.all([
    //       fetchProjects(),
    //       fetchNonAdminUsers(),
    //       fetchInitialData()
    //     ]);

    //     setProjects(projectsData);
    //     setNonAdminUsers(usersData);

    //     const transformedData = transformAssignmentData(
    //       assignmentsData,
    //       projectsData,
    //       usersData
    //     );
        
    //     // Filter assignments by client's project codes
    //     const clientProjectCodes = projectsData.map(p => p.project_code);
    //     const clientFilteredData = transformedData.filter(assignment => 
    //       clientProjectCodes.includes(assignment.project_code)
    //     );

    //     setData(clientFilteredData);
        
    //   } catch (error) {
    //     const errorMessage = handleApiError(error);
    //     setMsg(errorMessage);
    //     openPopup();
    //   } finally {
    //     setTimeout(() => {
    //       setLoader(false);
    //     }, 100);
    //   }
    // };

  //   const handleCreateState = async () => {
  //   if (showCreateUser) {
  //     // Create new user and assign
  //     if (!validateUserData()) return;
  //     setIsOpen(false);
      
  //     try {
  //       const token = localStorage.getItem("access_token");
  //       const decodedToken = jwtDecode(token);
  //       const create_form = {
  //         payload: {
  //           project_code: selectedProject.project_code,
  //           email: newUserData.email.trim().toLowerCase(),
  //           name: newUserData.name.trim(),
  //           password: newUserData.password,
  //           role: "User"
  //         },
  //         userInfo: {
  //           _id: sessionStorage.getItem("userid"),
  //           // clientId: sessionStorage.getItem('client'),
  //           clientId: decodedToken.client_id,
  //           // role: localStorage.getItem('role')
  //           role: decodedToken.role,
  //         }
  //       };

  //       await axios.post(`${PROJECT_URL}/projects/assign-user`, create_form, {
  //         headers: {
  //           // Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  //           Authorization: `Bearer ${token}`,
  //           // "x-resource-group": decodedToken.rg_name
  //           "x-resource-group": decodedToken.rg_name || "IXO-MeghnadNext-rg-dev-01",
  //         },
  //       });
        
  //       setMsg("User created and assigned successfully");
  //       setIsSuccess(true);
  //       setNewUserData({ name: '', email: '', password: '' });
  //       setShowCreateUser(false);
  //       ReloadData(false);
        
  //     } catch (error) {
  //       const errorMessage = handleApiError(error);
  //       setMsg(errorMessage);
  //       setIsSuccess(false);
  //     }
      
  //     setIsOpenPopup(true);
      
  //   } else {
  //     if(selectedUser.length > 0) {
  //       setIsOpen(false);
        
  //       // Create assignments one by one (since backend handles one user at a time)
  //       let successCount = 0;
  //       let errors = [];
        
  //       for (const user of selectedUser) {
  //         try {
  //           const create_form = {
  //             payload: {
  //               project_code: selectedProject.project_code,
  //               // email: user.user_id, // Backend expects email
  //               email: user.email, 
  //               role: "User"
  //             },
  //             userInfo: {
  //               // _id: localStorage.getItem("user_id"),
  //               _id: sessionStorage.getItem("userid"),
  //               clientId: sessionStorage.getItem('client'),
  //               role: localStorage.getItem('role')
  //             }
  //           };

  //           console.log("📤 Sending assignment request:", create_form);
            
  //           await axios.post(`${PROJECT_URL}/projects/assign-user`, create_form, {
  //             headers: {
  //               Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  //             },
  //           });
            
  //           successCount++;
            
  //         } catch (error) {
  //           console.error("❌ Assignment error:", error);
  //           errors.push(`${user.name}: ${handleApiError(error)}`);
  //         }
  //       }
        
  //       // Show results
  //       if (successCount > 0) {
  //         setMsg(`Successfully assigned ${successCount} user(s)${errors.length > 0 ? `. Failed: ${errors.join(', ')}` : ''}`);
  //         setIsSuccess(true);
  //         ReloadData(false);
  //       } else {
  //         setMsg(`Failed to assign users: ${errors.join(', ')}`);
  //         setIsSuccess(false);
  //       }
        
  //       setIsOpenPopup(true);
  //     }
  //   };
  // }

  // const handleEditState = async () => {
  //   let statu = { project_code: selectedProject.project_code, user_id: selectedUser.user_id };
  //   try {
  //     await axios
  //       .patch(`${API_URL}/projectusers/${rowData.id}`, statu, {
  //         headers: {
  //           Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  //         },
  //       })
  //       .then((response) => {
  //         setIsOpen(false);
  //         setMsg(response.data.message);
  //         setIsSuccess(true);
  //         ReloadData(false);
  //         setIsOpenPopup(true);
  //       });
  //   } catch (error) {
  //     setIsOpen(false);
  //     const errorMessage = handleApiError(error);
  //     setMsg(errorMessage);
  //     setIsSuccess(false);
  //     setIsOpenPopup(true);
  //   }
  // };
  
  // const handleCreateState = async () => {
  //   if(selectedUser.length > 0) {
  //     setIsOpen(false);
      
  //     // ✅ Decode JWT token to get rg_name
  //     const token = localStorage.getItem("access_token");
  //     const decodedToken = jwtDecode(token);
  //     console.log("RG:", decodedToken.rg_name);
      
  //     let successCount = 0;
  //     let errors = [];
      
  //     for (const user of selectedUser) {
  //       try {
  //         const create_form = {
  //           payload: {
  //             project_code: selectedProject.project_code,
  //             email: user.email, 
  //             role: "User"
  //           },
  //           userInfo: {
  //             _id: sessionStorage.getItem("userid"),
  //             clientId: decodedToken.client_id,  // ✅ Use from token
  //             role: decodedToken.role             // ✅ Use from token
  //           }
  //         };

  //         console.log("📤 Sending assignment request:", create_form);
          
  //         await axios.post(`${PROJECT_URL}/projects/assign-user`, create_form, {
  //           headers: {
  //             Authorization: `Bearer ${token}`,
  //             // "x-resource-group": decodedToken.rg_name || "IXO-MeghnadNext-rg-dev-01",  // ✅ Add this header
  //             "x-resource-group": decodedToken.rg_name.toLowerCase() || "IXO-MeghnadNext-rg-dev-01".toLowerCase()
  //           },
  //         });
          
  //         successCount++;
          
  //       } catch (error) {
  //         console.error("❌ Assignment error:", error);
  //         errors.push(`${user.name}: ${handleApiError(error)}`);
  //       }
  //     }
      
  //     // Show results
  //     if (successCount > 0) {
  //       setMsg(`Successfully assigned ${successCount} user(s)${errors.length > 0 ? `. Failed: ${errors.join(', ')}` : ''}`);
  //       setIsSuccess(true);
  //       ReloadData(false);
  //     } else {
  //       setMsg(`Failed to assign users: ${errors.join(', ')}`);
  //       setIsSuccess(false);
  //     }
      
  //     setIsOpenPopup(true);
  //   }
  // };

  // const handleCreateState = async () => {
  //   const token = localStorage.getItem("access_token");
  //   const decodedToken = jwtDecode(token);
  //   console.log("🔍 DEBUG - Token details:");
  //   console.log("- rg_name from token:", decodedToken.rg_name);
  //   console.log("- client_id:", decodedToken.client_id);
  //   console.log("- role:", decodedToken.role);

  //   // ----------------------------------------------
  //   // VALIDATION (Create New User Mode)
  //   // ----------------------------------------------
  //   if (showCreateUser) {
  //     let errors = {};

  //     if (!newUserData.name.trim()) errors.name = "Name is required";
  //     if (!newUserData.email.trim()) errors.email = "Email is required";
  //     if (!newUserData.password.trim()) errors.password = "Password is required";

  //     if (Object.keys(errors).length > 0) {
  //       setValidationErrors(errors);
  //       return; // 🚫 Prevent Save button from closing modal
  //     }

  //     setValidationErrors({});
  //   }

  //   // Close modal before API call
  //   setIsOpen(false);

  //   try {
  //     // ---------------------------------------------------
  //     // MODE 1: ASSIGN EXISTING USERS
  //     // ---------------------------------------------------
  //     if (!showCreateUser) {
  //       if (!selectedUser.length) {
  //         setMsg("Please select at least 1 user.");
  //         setIsSuccess(false);
  //         setIsOpenPopup(true);
  //         return;
  //       }

  //       let successCount = 0;

  //       for (const user of selectedUser) {
  //         const payload = {
  //           payload: {
  //             project_code: selectedProject.project_code,
  //             email: user.email,
  //             role: "User"
  //           },
  //           userInfo: {
  //             _id: sessionStorage.getItem("userid"),
  //             clientId: decodedToken.client_id,
  //             role: decodedToken.role
  //           }
  //         };

  //         await axios.post(`${PROJECT_URL}/projects/assign-user`, payload, {
  //           headers: {
  //             Authorization: `Bearer ${token}`,
  //             // "x-resource-group": decodedToken.rg_name || "IXO-MeghnadNext-rg-dev-01"
  //           }
  //         });

  //         successCount++;
  //       }

  //       setMsg(`Assigned ${successCount} user(s) successfully`);
  //       setIsSuccess(true);
  //       ReloadData(false);
  //       setIsOpenPopup(true);
  //       return;
  //     }

  //     // ---------------------------------------------------
  //     // MODE 2: CREATE NEW USER
  //     // ---------------------------------------------------
  //     if (showCreateUser) {
  //       const payload = {
  //         payload: {
  //           project_code: selectedProject.project_code,
  //           name: newUserData.name,
  //           email: newUserData.email,
  //           password: newUserData.password,
  //           role: "User"
  //         },
  //         userInfo: {
  //           _id: sessionStorage.getItem("userid"),
  //           clientId: decodedToken.client_id,
  //           role: decodedToken.role
  //         }
  //       };

  //       await axios.post(`${PROJECT_URL}/projects/assign-user`, payload, {
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //           // "x-resource-group": decodedToken.rg_name || "IXO-MeghnadNext-rg-dev-01"
  //         }
  //       });

  //       setMsg("User created and assigned successfully");
  //       setIsSuccess(true);
  //       ReloadData(false);
  //       setIsOpenPopup(true);
  //       return;
  //     }

  //   } catch (err) {
  //     setMsg(handleApiError(err));
  //     setIsSuccess(false);
  //     setIsOpenPopup(true);
  //   }
  // };
    const ReloadData = async (loadprops, waitForBackend = false) => {
      console.log("🟢 ReloadData START, loadprops:", loadprops);
      setLoader(loadprops);
      
      try {
        // Add a longer delay when a new user was just created to ensure backend consistency
        if (waitForBackend) {
          await new Promise(resolve => setTimeout(resolve, 4000));
        }
        console.log("🟢 Fetching projects...");
        const projectsData = await fetchProjects();
        console.log("🟢 Projects fetched:", projectsData);
        
        console.log("🟢 Fetching users...");
        const usersData = await fetchNonAdminUsers();
        console.log("🟢 Users fetched:", usersData);
        
        console.log("🟢 Fetching assignments...");
        const assignmentsData = await fetchInitialData();
        console.log("🟢 Assignments fetched:", assignmentsData);
        // Reload all data to get updated assignments and users
        // const [projectsData, usersData, assignmentsData] = await Promise.all([
        //   fetchProjects(),
        //   fetchNonAdminUsers(),
        //   fetchInitialData()
        // ]);

        console.log("🔄 Reloaded users data:", usersData);
        console.log("🔄 Reloaded assignments data:", assignmentsData);

        setProjects(projectsData);
        setNonAdminUsers(usersData);

        const transformedData = transformAssignmentData(
          assignmentsData,
          projectsData,
          usersData
        );
        
        // Filter assignments by client's project codes
        const clientProjectCodes = projectsData.map(p => p.project_code);
        const clientFilteredData = transformedData.filter(assignment => 
          clientProjectCodes.includes(assignment.project_code)
        );

        setData(clientFilteredData);
        
      } catch (error) {
        const errorMessage = handleApiError(error);
        setMsg(errorMessage);
        openPopup();
      } finally {
        setTimeout(() => {
          setLoader(false);
        }, 100);
      }
    };
  const handleCreateState = async () => {
    const token = localStorage.getItem("access_token");
    const decodedToken = jwtDecode(token);
    const userId = sessionStorage.getItem("userid") || decodedToken.sub;

    // Validation for Create New User Mode
    if (showCreateUser) {
      let errors = {};
      if (!newUserData.name.trim()) errors.name = "Name is required";
      if (!newUserData.email.trim()) errors.email = "Email is required";
      if (!newUserData.password.trim()) errors.password = "Password is required";

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return;
      }
      setValidationErrors({});
    }

    setIsOpen(false);
    setIsSubmitting(true);

    try {
      // ============================================================
      // MODE 1: ASSIGN EXISTING USERS
      // ============================================================
      if (!showCreateUser) {
        if (!selectedUser.length) {
          setMsg("Please select at least 1 user.");
          setIsSuccess(false);
          ReloadData(true);
          setIsOpenPopup(true);
          return;
        }

        let successCount = 0;

        for (const user of selectedUser) {
          // const payload = {
          //   payload: {
          //     project_code: selectedProject.project_code,
          //     email: user.email,
          //     role: "User"
          //   },
          //   userInfo: {
          //     _id: sessionStorage.getItem("userid"),
          //     clientId: decodedToken.client_id,
          //     role: decodedToken.role
          //   }
          // };

          // const payload = {
          //   project_code: selectedProject.project_code,
          //   email: user.email,
          //   role: "User"
          // };

          const payload = {
            payload: {  // ← ADD THIS WRAPPER
              project_code: selectedProject.project_code,
              email: user.email,
              role: "User"
            },
            userInfo: {  // ← ADD THIS
              user_id: userId,
              _id: userId,
              clientId: decodedToken.client_id,
              role: decodedToken.role
            }
          };

          console.log("📤 MODE 1 - Payload being sent:", JSON.stringify(payload, null, 2));
          console.log("📤 MODE 1 - Project code:", selectedProject.project_code);

          // await axios.post(`${PROJECT_URL}/projects/assign-user`, payload, {
          await axios.post(`${PROJECT_URL}/projects/assign-user`, payload, {
            headers: {
              Authorization: `Bearer ${token}`,
              "x-resource-group": decodedToken.rg_name || "IXO-MeghnadNext-rg-dev-01"
            }
          });

          successCount++;
        }

        setMsg(`Assigned ${successCount} user(s) successfully`);
        setIsSuccess(true);
        // ReloadData(false);
        setIsSubmitting(false);
        ReloadData(true);
        setIsOpenPopup(true);
        return;
      }

      // ============================================================
      // MODE 2: CREATE NEW USER
      // ============================================================
      if (showCreateUser) {
        // const payload = {
        //   payload: {
        //     project_code: selectedProject.project_code,
        //     name: newUserData.name,
        //     email: newUserData.email,
        //     password: newUserData.password,
        //     role: "User"
        //   },
        //   userInfo: {
        //     _id: sessionStorage.getItem("userid"),
        //     clientId: decodedToken.client_id,
        //     role: decodedToken.role
        //   }
        // };

        // const payload = {
        //   project_code: selectedProject.project_code,
        //   name: newUserData.name,
        //   email: newUserData.email,
        //   password: newUserData.password,
        //   role: "User"
        // };

        const payload = {
          payload: {
            project_code: selectedProject.project_code,
            name: newUserData.name,
            email: newUserData.email,
            password: newUserData.password,
            role: "User"
          },
          userInfo: {
            // user_id: sessionStorage.getItem("userid"),
            user_id: userId,
            // _id: sessionStorage.getItem("userid"),
            _id: userId,
            clientId: decodedToken.client_id,
            role: decodedToken.role
          }
        };

        console.log("📤 Final payload being sent:", payload);
        console.log("📤 Headers being sent:", {
          Authorization: `Bearer ${token}`,
          "x-resource-group": decodedToken.rg_name || "IXO-MeghnadNext-rg-dev-01"
        });

        // await axios.post(`${PROJECT_URL}/projects/assign-user`, payload, {
        const response = await axios.post(`${PROJECT_URL}/projects/assign-user`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-resource-group": decodedToken.rg_name || "IXO-MeghnadNext-rg-dev-01"
          }
        });

        console.log("✅ Assignment created, response:", response.data);
        console.log("✅ Created for client_id:", decodedToken.client_id);

        setMsg("User created and assigned successfully");
        setIsSuccess(true);
        // ReloadData(false);
        setIsSubmitting(false);
        ReloadData(true);
        setIsOpenPopup(true);
        return;
      }

    // } catch (err) {
    //   console.error("❌ Full error:", err);
    //   console.error("❌ Error response:", err.response?.data);
    //   console.error("❌ Error status:", err.response?.status);

    //   let errorMsg = "";

    //   // Case 1: FastAPI error → { detail: "message" }
    //   if (err?.response?.data?.detail) {
    //     errorMsg = err.response.data.detail;
    //   }
    //   // Case 2: Backend returns plain string body
    //   else if (typeof err?.response?.data === "string") {
    //     errorMsg = err.response.data;
    //   }
    //   // Case 3: Backend returns nested message
    //   else if (err?.response?.data?.message) {
    //     errorMsg = err.response.data.message;
    //   }
    //   // Fallback only if nothing available
    //   else {
    //     errorMsg = "Unexpected error occurred";
    //   }

    //   setMsg(errorMsg);
    //   setIsSuccess(false);
    //   setIsOpenPopup(true);
    // }
    } catch (error) {
      console.error("❌ Create error:", error);
      console.error("❌ Error response:", error.response?.data);
      
      let errorMessage = handleApiError(error);
      
      // Handle validation errors from backend
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(err => err.msg).join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      }
      setIsSubmitting(false);
      setMsg(errorMessage);
      setIsSuccess(false);
      setIsOpenPopup(true);
    }
  };

  const handleEditState = async () => {
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("access_token");
      const decodedToken = jwtDecode(token);
      // For edit, we need to delete old assignment and create new one
      // Since your backend doesn't have update endpoint
      
      // Step 1: Delete old assignment
      // await axios.delete(`${PROJECT_URL}/projects/assign-user/${rowData.id}`, {
      await axios.delete(`${PROJECT_URL}/projects/assign-user/${rowData.id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "x-resource-group": decodedToken.rg_name || "IXO-MeghnadNext-rg-dev-01"
        },
        data: {
          payload: {},
          userInfo: {
            _id: decodedToken.user_id || decodedToken.sub,
            clientId: decodedToken.client_id,
            role: decodedToken.role
          }
        }
        // data: {
        //   payload: {},
        //   userInfo: {
        //     // _id: localStorage.getItem("user_id"),
        //     _id: sessionStorage.getItem("userid"),
        //     clientId: sessionStorage.getItem('client'),
        //     role: localStorage.getItem('role')
        //   }
        // }
      });
      
      // Step 2: Create new assignment
      // const create_form = {
      //   payload: {
      //     project_code: selectedProject.project_code,
      //     // email: selectedUser.user_id,
      //     email: selectedUser.email,
      //     role: "User"
      //   },
      //   userInfo: {
      //     // _id: localStorage.getItem("user_id"),
      //     _id: sessionStorage.getItem("userid"),
      //     // clientId: sessionStorage.getItem('client'),
      //     clientId: decodedToken.client_id,
      //     // role: localStorage.getItem('role')
      //     role: decodedToken.role
      //   }
      // };
      const create_form = {
        payload: {
          project_code: selectedProject.project_code,
          email: selectedUser.email,
          role: "User"
        },
        userInfo: {
          _id: decodedToken.user_id || decodedToken.sub,
          clientId: decodedToken.client_id,
          role: decodedToken.role
        }
      };

      console.log("📤 Sending edit request:", create_form);
      
      // await axios.post(`${PROJECT_URL}/projects/assign-user`, create_form, {
      await axios.post(`${PROJECT_URL}/projects/assign-user`, create_form, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "x-resource-group": decodedToken.rg_name || "IXO-MeghnadNext-rg-dev-01"
        },
      });
      setIsOpen(false);
      setMsg("Assignment updated successfully");
      setIsSubmitting(false); 
      setIsSuccess(true);
      ReloadData(false);
      setIsOpenPopup(true);
      
    } catch (error) {
      setIsOpen(false);
      setIsSubmitting(false); 
      console.error("❌ Edit error:", error);
      const errorMessage = handleApiError(error);
      setMsg(errorMessage);
      setIsSuccess(false);
      setIsOpenPopup(true);
    }
  };

  let [isOpenStatusModal, setIsOpenStatusModal] = useState(false);
  let [modalTitle, setModalTitle] = useState('');

  function closeStatusModal() {
    setIsOpenStatusModal(false);
  }

  function openStatusModal(e) {
    setRowData(e);
    setModalTitle(`Update Project assignment status`)
    setIsOpenStatusModal(true);
  }

  // const handleStatusState = async () => {
  //   let statusData = { status: rowData.status === false || rowData.status === 'false' ? false : true };
  //   try {
  //     await axios
  //       .patch(`${API_URL}/projectusers/status/${rowData.id}`, statusData, {
  //         headers: {
  //           Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  //         },
  //       })
  //       .then((response) => {
  //         setIsOpenStatusModal(false);
  //         setMsg(response.data.message);
  //         setIsSuccess(true);
  //         ReloadData(false);
  //         setIsOpenPopup(true);
  //       });
  //   } catch (error) {
  //     setIsOpenStatusModal(false);
  //     const errorMessage = handleApiError(error);
  //     setMsg(errorMessage);
  //     setIsSuccess(false);
  //     setIsOpenPopup(true);
  //   }
  // };

  const handleStatusState = async () => {
  try {
    const token = localStorage.getItem("access_token");
    const decodedToken = jwtDecode(token);
    // Convert status back to 'active' or 'inactive' string
    const newStatus = (rowData.status === true || rowData.status === 'true') ? 'active' : 'inactive';
    
    // const statusData = {
    //   payload: {
    //     status: newStatus
    //   },
    //   userInfo: {
    //     _id: localStorage.getItem("user_id"),
    //     clientId: sessionStorage.getItem('client'),
    //     role: localStorage.getItem('role')
    //   }
    // };

    const statusData = {
      status: newStatus
    };
    
    // await axios.patch(`${PROJECT_URL}/projects/assign-user/status/${rowData.id}`, statusData, {
    await axios.patch(`${PROJECT_URL}/projects/assign-user/status/${rowData.id}`, statusData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        "x-resource-group": decodedToken.rg_name || "IXO-MeghnadNext-rg-dev-01"
      },
    });
    
    setIsOpenStatusModal(false);
    setMsg("Status updated successfully");
    setIsSuccess(true);
    ReloadData(false);
    setIsOpenPopup(true);
    
  } catch (error) {
    setIsOpenStatusModal(false);
    const errorMessage = handleApiError(error);
    setMsg(errorMessage);
    setIsSuccess(false);
    setIsOpenPopup(true);
  }
};

  const [isOpenConfirmPopup, setIsOpenConfirmPopup] = useState(false);
  const [confirmationMsg, setConfirmationMsg] = useState("");

  function closeConfirmPopup() {
    setIsOpenConfirmPopup(false);
  }

  function openConfirmPopup(e) {
    setRowData(e);
    setConfirmationMsg("Are you sure that you want to delete that project assignment?")
    setIsOpenConfirmPopup(true);
  }

  // const handleDeleteState = async () => {
  //   try {
  //     await axios
  //       .delete(`${API_URL}/projectusers/${rowData.id}`, {
  //         headers: {
  //           Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  //         },
  //       })
  //       .then((response) => {
  //         setIsOpenConfirmPopup(false);
  //         setMsg(response.data.message);
  //         setIsSuccess(true);
  //         ReloadData(false);
  //         setIsOpenPopup(true);
  //       });
  //   } catch (error) {
  //     setIsOpenConfirmPopup(false);
  //     const errorMessage = handleApiError(error);
  //     setMsg(errorMessage);
  //     setIsSuccess(false);
  //     setIsOpenPopup(true);
  //   }
  // };

  const handleDeleteState = async () => {
  try {
    const token = localStorage.getItem("access_token");
    const decodedToken = jwtDecode(token);
    // await axios.delete(`${PROJECT_URL}/projects/assign-user/${rowData.id}`, {
    await axios.delete(`${PROJECT_URL}/projects/assign-user/${rowData.id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        // "x-resource-group": decodedToken.rg_name || "IXO-MeghnadNext-rg-dev-01"
      },
      // data: {
      //   payload: {},
      //   userInfo: {
      //     _id: localStorage.getItem("user_id"),
      //     clientId: sessionStorage.getItem('client'),
      //     role: localStorage.getItem('role')
      //   }
      // }
    });
    
    setIsOpenConfirmPopup(false);
    setMsg("Assignment deleted successfully");
    setIsSuccess(true);
    ReloadData(false);
    setIsOpenPopup(true);
    
  } catch (error) {
    setIsOpenConfirmPopup(false);
    const errorMessage = handleApiError(error);
    setMsg(errorMessage);
    setIsSuccess(false);
    setIsOpenPopup(true);
  }
};

  const [sorting, setSorting] = React.useState([]);

  const columns = React.useMemo(
    () => [
      {
        accessorKey: 'index',
        enableColumnFilter: false,
        enableSorting: false,
        size: 50,
        header: '#',
        cell: ({ row }) => <center>{row.index + 1}</center>,
      },
      {
        accessorKey: 'project_name',
        enableColumnFilter: true,
        size: 100,
        header: () => <span>Project Name</span>
      },
      // {
      //   accessorKey: 'user_name',
      //   enableColumnFilter: true,
      //   enableSorting: false,
      //   size: 200,
      //   header: () => <span>User</span>,
      //   cell: ({ row }) => (
      //     <div className="text-start">
      //       <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-0 m-1 text-sm font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
      //         {row.original.user_id} - {row.original.name}
      //       </span>
      //     </div>
      //   ),
      // },
      {
        accessorKey: 'user_name',
        enableColumnFilter: true,
        enableSorting: false,
        size: 200,
        header: () => <span>User</span>,
        cell: ({ row }) => (
          <div className="text-start">
            <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-0 m-1 text-sm font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
              {row.original.user_name}
              {row.original.user_id && row.original.user_id !== row.original.user_name && (
                <span className="ml-1 text-xs text-gray-500">({row.original.user_id})</span>
              )}
            </span>
          </div>
        ),
      },
      {
        accessorKey: 'creation_date',
        enableColumnFilter: false,
        size: 150,
        header: 'Created Date',
        cell: ({ row }) => (
          <div>{formattedDate(row.original.creation_date)}</div>
        )
      },
      {
        // accessorKey: 'creation_by',
        accessorKey: 'assigned_by_name',
        enableColumnFilter: false,
        size: 50,
        header: 'Created By',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 50,
        enableColumnFilter: true,
        enableSorting: false,
        cell: ({ row }) => (
          <div className='flex justify-center'><div onClick={() => openStatusModal(row.original)} className={`${row.original.status === true ? 'bg-green-300  text-green-900' : 'bg-red-300 text-red-900'} text-xs w-fit px-2 py-1 font-bold rounded-xl cursor-default lg:cursor-pointer`}>{row.original.status === true ? "Active" : "Inactive"}</div></div>
        )
      },
      {
        accessorKey: 'id',
        header: 'Actions',
        size: 50,
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className='flex gap-2 items-center justify-center'>
            <Menu as="div" ref={(el) => (buttonRefs.current[row.original.id] = el)}>
              <div>
                <Menu.Button className="border bg-gray-50 rounded-md p-1 text-blue-800 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 cursor-default lg:cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    calculatePosition(row.original.id)
                  }
                  }
                >
                  <EllipsisHorizontalIcon className="h-4 text-black" />
                </Menu.Button>
              </div>
              <Portal>
                <Transition
                  as={Fragment}
                  show={activeDropdown === row.original.id}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute z-30 right-8 md:right-16 mt-1 w-fit rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                    style={{
                      top: `${dropdownPosition.top}px`,
                    }}
                    ref={dropdownRef}
                  >
                    <button
                      className="flex items-center px-4 py-2 hover:bg-gray-200 hover:text-blue-800 w-full text-sm"
                      onClick={() => {
                        openModal(row.original, "edit")
                        setActiveDropdown(null)
                      }}
                    >
                      <PencilIcon className="h-4 text-blue-800 mr-2" />
                      <span>Edit</span>
                    </button>
                    {localStorage.getItem('role') === 'ADMIN' &&
                      <button className="flex items-center px-4 py-2 hover:bg-gray-200 hover:text-red-800 w-full text-sm"
                        onClick={() => {
                          openConfirmPopup(row.original)
                          setActiveDropdown(null)
                        }}>
                        <TrashIcon className='h-4 text-red-800 mr-2 rounded-md border' />
                        <span>Delete</span>
                      </button>}
                  </Menu.Items>
                </Transition>
              </Portal>
            </Menu>
          </div>
        )
      },
    ],
    [openModal, activeDropdown, calculatePosition, dropdownPosition.top]
  );

  const [data, setData] = useState([]);

  const [globalFilter, setGlobalFilter] = React.useState('');
  const [pageIndex, setPageIndex] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(initialpageSize);


  const pagination = React.useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  )

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination,
      globalFilter
    },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues()
  });

  const tableContainerRef = React.useRef(null);
  // Modify the pagination section
  const pageCount = Math.ceil(data.length / pageSize);
  const pageNumbers = [];

  for (let i = 0; i < pageCount; i++) {
    pageNumbers.push(i);
  }

  const handlePageChange = (e) => {
    const newPageSize = Number(e.target.value);
    setPageSize(newPageSize);
    setPageIndex(0)
  };

  const { rows } = table.getRowModel();
  // const rowVirtualizer = useVirtualizer({
  //   parentRef: tableContainerRef,
  //   size: rows.length,
  //   overscan: pageSize,
  // });
  // const rowVirtualizer = useVirtualizer({
  //   count: rows.length,
  //   getScrollElement: () => tableContainerRef.current,
  //   estimateSize: () => 50,
  //   overscan: 5,
  // });
  // // const { virtualItems: virtualRows, totalSize } = rowVirtualizer;
  // const { virtualItems: virtualRows = [], totalSize = 0 } = rowVirtualizer || {};

  // const paddingTop = virtualRows.length > 0 ? virtualRows[0]?.start || 0 : 0;
  // const paddingBottom =
  //   virtualRows.length > 0
  //     ? totalSize - (virtualRows[virtualRows.length - 1]?.end || 0)
  //     : 0;
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 50,
    overscan: 5,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const paddingTop = virtualRows.length > 0 ? virtualRows[0]?.start || 0 : 0;
  const paddingBottom = virtualRows.length > 0
    ? totalSize - (virtualRows[virtualRows.length - 1]?.end || 0)
    : 0;

  const renderTableCellIndex = (row) => {
    return (
      table.getFilteredRowModel().rows.indexOf(row) + 1 ||
      table.getSortedRowModel().rows.indexOf(row) + 1 ||
      row.index + 1
    );
  };

  // useEffect(() => {
  //   const fetchInitialData = async () => {
  //     axios
  //       .get(`${API_URL}/projectusers`, {
  //         headers: {
  //           Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  //         },
  //         params: {
  //           client: sessionStorage.getItem('client')
  //         }
  //       })
  //       .then((response) => {
  //         setData(response.data.data);
  //       })
  //       .catch((error) => {
  //         const errorMessage = handleApiError(error);
  //         setMsg(errorMessage);
  //         openPopup();
  //       })
  //       .finally(() => {
  //         setTimeout(() => {
  //           setLoader(false);
  //         }, 100);
  //       });
  //   };
  //   const fetchNonAdminUsers = async () => {
  //     axios
  //       .get(`${API_URL}/users/nonadmin`, {
  //         headers: {
  //           Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  //         },
  //         params: {
  //           client: sessionStorage.getItem('client')
  //         }
  //       })
  //       .then((response) => {
  //         setNonAdminUsers(response.data.data);
  //       })
  //       .catch((error) => {
  //         const errorMessage = handleApiError(error);
  //         setMsg(errorMessage);
  //         openPopup();
  //       });
  //   };
  //   const fetchProjects = async () => {
  //     axios
  //       .get(`${API_URL}/projects`, {
  //         headers: {
  //           Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  //         },
  //         params: {
  //           client: sessionStorage.getItem('client')
  //         }
  //       })
  //       .then((response) => {
  //         setProjects(response.data.data);
  //       })
  //       .catch((error) => {
  //         const errorMessage = handleApiError(error);
  //         setMsg(errorMessage);
  //         openPopup();
  //       });
  //   };
  //   fetchInitialData();
  //   fetchNonAdminUsers();
  //   fetchProjects();
  // }, []);

//   useEffect(() => {
//   // const fetchInitialData = async () => {
//   //   try {
//   //     const response = await axios.post(`${PROJECT_URL}/projects/list-users`, {
//   //       payload: {},
//   //       userInfo: {
//   //         _id: localStorage.getItem("user_id"),
//   //         clientId: sessionStorage.getItem('client'),
//   //         role: localStorage.getItem('role')
//   //       }
//   //     }, {
//   //       headers: {
//   //         Authorization: `Bearer ${localStorage.getItem("access_token")}`,
//   //       }
//   //     });
      
//   //     // Transform data after all data is loaded
//   //     const transformedData = transformAssignmentData(
//   //       response.data,
//   //       projects,
//   //       nonAdminUsers
//   //     );
      
//   //     setData(transformedData);
      
//   //   } catch (error) {
//   //     const errorMessage = handleApiError(error);
//   //     setMsg(errorMessage);
//   //     openPopup();
//   //   } finally {
//   //     setTimeout(() => {
//   //       setLoader(false);
//   //     }, 100);
//   //   }
//   // };
  
//   // const fetchNonAdminUsers = async () => {
//   //   axios
//   //     .get(`${PROJECT_URL}/users/nonadmin`, {
//   //       headers: {
//   //         Authorization: `Bearer ${localStorage.getItem("access_token")}`,
//   //       },
//   //       params: {
//   //         client: sessionStorage.getItem('client')
//   //       }
//   //     })
//   //     .then((response) => {
//   //       setNonAdminUsers(response.data.data);
//   //     })
//   //     .catch((error) => {
//   //       const errorMessage = handleApiError(error);
//   //       setMsg(errorMessage);
//   //       openPopup();
//   //     });
//   // };
  
//   const fetchInitialData = async () => {
//     try {
//       const response = await axios.post(
//         `${PROJECT_URL}/projects/list-users`,
//         {
//           payload: {},
//           userInfo: {
//             _id: localStorage.getItem("user_id"),
//             clientId: sessionStorage.getItem("client"),
//             role: localStorage.getItem("role"),
//           }
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${localStorage.getItem("access_token")}`,
//           }
//         }
//       );

//       // ❗ Very important: use latest updated state values
//       const transformedData = transformAssignmentData(
//         response.data,
//         projects,        // now correctly loaded
//         nonAdminUsers    // now correctly loaded
//       );

//       setData(transformedData);
//     } catch (error) {
//       const errorMessage = handleApiError(error);
//       setMsg(errorMessage);
//       openPopup();
//     }
//   };

//   const fetchNonAdminUsers = async () => {
//     try {
//       const token = localStorage.getItem("access_token");
//       const decodedToken = jwtDecode(token);

//       const payload = {
//         payload: { project_code: null },
//         userInfo: {
//           _id: decodedToken.sub,
//           role: decodedToken.role,
//           clientId: decodedToken.client_id,
//         },
//       };

//       const response = await axios.post(
//         `${PROJECT_URL}/projects/list-users`,
//         payload,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`
//           }
//         }
//       );

//       setNonAdminUsers(response.data || []);
//     } catch (error) {
//       console.error("Error fetching non-admin users:", error);
//     }
//   };


//   // const fetchProjects = async () => {
//   //   axios
//   //     .get(`${PROJECT_URL}/projects`, {
//   //       headers: {
//   //         Authorization: `Bearer ${localStorage.getItem("access_token")}`,
//   //       },
//   //       params: {
//   //         client: sessionStorage.getItem('client')
//   //       }
//   //     })
//   //     .then((response) => {
//   //       setProjects(response.data.data);
//   //     })
//   //     .catch((error) => {
//   //       const errorMessage = handleApiError(error);
//   //       setMsg(errorMessage);
//   //       openPopup();
//   //     });
//   // };
//   // Fetch in sequence to ensure projects and users are loaded before assignments
//   const fetchProjects = async () => {
//     try {
//       const token = localStorage.getItem("access_token");
//       const decodedToken = jwtDecode(token);

//       const payload = {
//         payload: {},
//         userInfo: {
//           _id: decodedToken.sub,
//           role: decodedToken.role,
//           clientId: decodedToken.client_id,
//         },
//       };

//       const response = await axios.post(
//         `${PROJECT_URL}/projects/list`,
//         payload,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`
//           }
//         }
//       );

//       setProjects(response.data || []);
//     } catch (error) {
//       console.error("Error fetching projects:", error);
//     }
//   };

  
//   // const loadAllData = async () => {
//   //   await fetchProjects();
//   //   await fetchNonAdminUsers();
//   //   await fetchInitialData();
//   // };
//   // const loadAllData = async () => {
//   //   // try {
//   //   //   setLoader(true);

//   //   //   await Promise.all([
//   //   //     fetchProjects(),
//   //   //     fetchNonAdminUsers(),
//   //   //   ]);

//   //   //   setLoader(false);
//   //   // } catch (e) {
//   //   //   console.error("Error loading data:", e);
//   //   //   setLoader(false);
//   //   // }
//   //   await fetchProjects();
//   //   await fetchNonAdminUsers();
//   //   await fetchInitialData();
//   // };

//   // const loadAllData = async () => {
//   //   try {
//   //     setLoader(true);

//   //     // Load projects and users first (important!)
//   //     await fetchProjects();
//   //     await fetchNonAdminUsers();

//   //     console.log("Projects loaded:", projects);
//   //     console.log("Users loaded:", nonAdminUsers);

//   //     // Now fetch assignments, which depends on the above data
//   //     await fetchInitialData();

//   //   } catch (err) {
//   //     console.error("Error loading all data:", err);
//   //   } finally {
//   //     setLoader(false);
//   //   }
//   // };

//   const loadAllData = async () => {
//     try {
//       setLoader(true);

//       // Load all data
//       const [projectsData, usersData, assignmentsData] = await Promise.all([
//         fetchProjects(),
//         fetchNonAdminUsers(),
//         fetchInitialData()
//       ]);

//       setProjects(projectsData);
//       setNonAdminUsers(usersData);

//       // ✅ Simplified - no need to pass projects/users since backend provides everything
//       const transformedData = transformAssignmentData(assignmentsData);

//       setData(transformedData);

//     } catch (err) {
//       console.error("Error loading all data:", err);
//     } finally {
//       setLoader(false);
//     }
//   };
  
//   loadAllData();
// }, []); // Empty dependency array - only run once on mount
  
//   useEffect(() => {
//   const fetchProjects = async () => {
//     try {
//       const token = localStorage.getItem("access_token");
//       const decodedToken = jwtDecode(token);

//       const payload = {
//         payload: {},
//         userInfo: {
//           _id: decodedToken.sub,
//           role: decodedToken.role,
//           clientId: decodedToken.client_id,
//         },
//       };

//       const response = await axios.post(
//         `${PROJECT_URL}/projects/list`,
//         payload,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`
//           }
//         }
//       );

//       return response.data || [];
//     } catch (error) {
//       console.error("Error fetching projects:", error);
//       return [];
//     }
//   };

//   const fetchNonAdminUsers = async () => {
//     try {
//       const token = localStorage.getItem("access_token");
//       const decodedToken = jwtDecode(token);

//       const payload = {
//         payload: { project_code: null },
//         userInfo: {
//           _id: decodedToken.sub,
//           role: decodedToken.role,
//           clientId: decodedToken.client_id,
//         },
//       };

//       const response = await axios.post(
//         `${PROJECT_URL}/projects/list-users`,
//         payload,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`
//           }
//         }
//       );

//       return response.data || [];
//     } catch (error) {
//       console.error("Error fetching non-admin users:", error);
//       return [];
//     }
//   };

//   const fetchInitialData = async () => {
//     try {
//       const response = await axios.post(
//         `${PROJECT_URL}/projects/list-users`,
//         {
//           payload: {},
//           userInfo: {
//             _id: localStorage.getItem("user_id"),
//             clientId: sessionStorage.getItem("client"),
//             role: localStorage.getItem("role"),
//           }
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${localStorage.getItem("access_token")}`,
//           }
//         }
//       );

//       return response.data || [];
//     } catch (error) {
//       const errorMessage = handleApiError(error);
//       setMsg(errorMessage);
//       openPopup();
//       return [];
//     }
//   };

//   const loadAllData = async () => {
//     try {
//       setLoader(true);

//       // ✅ Load projects and users FIRST and WAIT for them
//       const [projectsData, usersData, assignmentsData] = await Promise.all([
//         fetchProjects(),
//         fetchNonAdminUsers(),
//         fetchInitialData()
//       ]);

//       // ✅ Set state AFTER all data is loaded
//       setProjects(projectsData);
//       setNonAdminUsers(usersData);

//       // ✅ Now transform with the loaded data
//       const transformedData = transformAssignmentData(
//         assignmentsData,
//         projectsData,  // Use the fetched data, not state
//         usersData      // Use the fetched data, not state
//       );

//       setData(transformedData);

//     } catch (err) {
//       console.error("Error loading all data:", err);
//     } finally {
//       setLoader(false);
//     }
//   };

//   const userId = sessionStorage.getItem("userid");
//   if (!userId) return;

//   loadAllData();
// }, []); // Empty dependency array - only run once on mount
  
  useEffect(() => {

    // const fetchProjects = async () => {
    //   try {
    //     const token = localStorage.getItem("access_token");
    //     const decodedToken = jwtDecode(token);

    //     const payload = {
    //       payload: null,   // FIXED
    //       // payload: { project_code: null },
    //       userInfo: {
    //         _id: decodedToken.sub,
    //         role: decodedToken.role,
    //         clientId: decodedToken.client_id,
    //       },
    //     };

    //     const response = await axios.post(
    //       `${PROJECT_URL}/projects/list`,
    //       payload,
    //       {
    //         headers: {
    //           Authorization: `Bearer ${token}`
    //         }
    //       }
    //     );

    //     return response.data || [];
    //   } catch (error) {
    //     console.error("Error fetching projects:", error);
    //     return [];
    //   }
    // };

    // const fetchNonAdminUsers = async () => {
    //   try {
    //     const token = localStorage.getItem("access_token");
    //     const decodedToken = jwtDecode(token);

    //     const payload = {
    //       payload: { project_code: null },
    //       userInfo: {
    //         _id: decodedToken.sub,
    //         role: decodedToken.role,
    //         clientId: decodedToken.client_id,
    //       },
    //     };

    //     const response = await axios.post(
    //       `${PROJECT_URL}/projects/list-users`,
    //       payload,
    //       {
    //         headers: {
    //           Authorization: `Bearer ${token}`
    //         }
    //       }
    //     );

    //     return response.data || [];
    //   } catch (error) {
    //     console.error("Error fetching non-admin users:", error);
    //     return [];
    //   }
    // };

    // Replace your fetchNonAdminUsers function with this:

    // ============================================================
// REPLACE YOUR fetchNonAdminUsers FUNCTION IN ProjectAssignment.js
// ============================================================

    // const fetchNonAdminUsers = async () => {
    //   try {
    //     const token = localStorage.getItem("access_token");
    //     const decodedToken = jwtDecode(token);

    //     const payload = {
    //       payload: {},
    //       userInfo: {
    //         _id: decodedToken.sub,
    //         role: decodedToken.role,
    //         clientId: decodedToken.client_id,
    //       },
    //     };

    //     const response = await axios.post(
    //       `${PROJECT_URL}/projects/users/list`,
    //       payload,
    //       {
    //         headers: {
    //           Authorization: `Bearer ${token}`
    //         }
    //       }
    //     );

    //     const users = response.data || [];
        
    //     console.log("✅ Users loaded:", users);
        
    //     // ✅ Format users correctly for the dropdown
    //     const formattedUsers = users.map(user => ({
    //       user_id: user.email,      // Use email as user_id (for filtering/matching)
    //       name: user.name,           // Display name
    //       email: user.email,         // Keep email for assignment
    //       _id: user._id,            // Keep original _id
    //       role: user.role           // Keep role
    //     }));
        
    //     console.log("✅ Formatted users:", formattedUsers);
        
    //     return formattedUsers;

    //   } catch (error) {
    //     console.error("❌ Error fetching users:", error);
    //     return [];
    //   }
    // };

    // const fetchNonAdminUsers = async () => {
    //   try {
    //     const token = localStorage.getItem("access_token");
    //     const decodedToken = jwtDecode(token);

    //     const payload = {
    //       payload: {},
    //       userInfo: {
    //         _id: decodedToken.sub,
    //         role: decodedToken.role,
    //         clientId: decodedToken.client_id,
    //       },
    //     };

    //     // ✅ Use correct endpoint that returns ALL users for the client
    //     const response = await axios.post(
    //       `${PROJECT_URL}/projects/users/list`,
    //       payload,
    //       {
    //         headers: {
    //           Authorization: `Bearer ${token}`
    //         }
    //       }
    //     );

    //     // const users = response.data || [];
        
    //     // console.log("✅ Users loaded:", users);
        
    //     // // ✅ Format users correctly
    //     // const formattedUsers = users.map(user => ({
    //     //   _id: user._id,              // Keep MongoDB ObjectId for matching
    //     //   user_id: user.email,         // Use email as user_id
    //     //   name: user.name,             // Display name
    //     //   email: user.email,           // Keep email for assignment
    //     //   role: user.role              // Keep role
    //     // }));

    //     const users = response.data || [];
    
    //     // ✅ Additional client filtering if backend doesn't handle it
    //     const currentClientId = sessionStorage.getItem('client');
    //     const filteredUsers = users.filter(user => 
    //       user.client_id === currentClientId || 
    //       user.clientId === currentClientId
    //     );
        
    //     console.log("✅ Client-filtered users:", filteredUsers);
        
    //     const formattedUsers = filteredUsers.map(user => ({
    //       _id: user._id,
    //       user_id: user.email,
    //       name: user.name,
    //       email: user.email,
    //       role: user.role
    //     }));
        
    //     console.log("✅ Formatted users:", formattedUsers);
        
    //     return formattedUsers;

    //   } catch (error) {
    //     console.error("❌ Error fetching users:", error);
    //     return [];
    //   }
    // };

    // const fetchInitialData = async () => {
    //   try {
    //     const token = localStorage.getItem("access_token");
    //     const decodedToken = jwtDecode(token);
    //     const response = await axios.post(
    //       `${PROJECT_URL}/projects/list-users`,
    //       {
    //         // payload: null,  // FIXED
    //         payload: { project_code: null },
    //         userInfo: {
    //           _id: sessionStorage.getItem("userid"),   // FIXED — MUST use sessionStorage
    //           // clientId: sessionStorage.getItem("client"),
    //           clientId: decodedToken.client_id,
    //           // role: sessionStorage.getItem("role"),
    //           role: decodedToken.role,
    //         }
    //       },
    //       {
    //         headers: {
    //           Authorization: `Bearer ${localStorage.getItem("access_token")}`,
    //         }
    //       }
    //     );

    //     return response.data || [];
    //   } catch (error) {
    //     const errorMessage = handleApiError(error);
    //     setMsg(errorMessage);
    //     openPopup();
    //     return [];
    //   }
    // };

    const loadAllData = async () => {
      try {
        setLoader(true);

        const [projectsData, usersData, assignmentsData] = await Promise.all([
          fetchProjects(),
          fetchNonAdminUsers(),
          fetchInitialData()
        ]);

        setProjects(projectsData);
        setNonAdminUsers(usersData);

        const transformedData = transformAssignmentData(
          assignmentsData,
          projectsData,
          usersData
        );
        // ✅ DEBUG: Log the data to see the structure
        console.log("🔍 Projects data:", projectsData);
        console.log("🔍 Assignments data:", assignmentsData);
        console.log("🔍 Transformed data:", transformedData);

        // ✅ Filter assignments to only show current client's projects
        // const currentClientId = sessionStorage.getItem('client');
        // const clientFilteredData = transformedData.filter(assignment => {
        //   const project = projectsData.find(p => p.project_code === assignment.project_code);
        //   return project && (project.client_id === currentClientId || project.clientId === currentClientId);
        // });

        // console.log("✅ Client-filtered assignments:", clientFilteredData);
        
        // ✅ Filter assignments by your client's project codes
        const clientProjectCodes = projectsData.map(p => p.project_code);
        const clientFilteredData = transformedData.filter(assignment => 
          clientProjectCodes.includes(assignment.project_code)
        );

        console.log("✅ Client project codes:", clientProjectCodes);
        console.log("✅ Client-filtered assignments:", clientFilteredData);

        setData(clientFilteredData);
        // ✅ Check what client field exists in projects
        if (projectsData.length > 0) {
          console.log("🔍 Project fields:", Object.keys(projectsData[0]));
        }

      } catch (err) {
        console.error("Error loading all data:", err);
      } finally {
        setLoader(false);
      }
    };

    // ✅ ADD THIS GUARD HERE
    const userId = sessionStorage.getItem("userid");
    if (!userId) return;   // ⛔ Skip API calls until userid is available

    loadAllData();

  }, [sessionStorage.getItem("userid")]);

  useEffect(() => {
    if(edit===false){
      
      // Extract user_ids and project_codes from the data array
      let existingUserIds = data?.filter((item) => item.project_code === selectedProject.project_code).map((item) => item.user_id);

      // Filter unmatched users
      const unmatched = nonAdminUsers?.filter(
        (user) =>
          !existingUserIds.includes(user.user_id)
      );

      // Update state
      setUnmatchedUsers(unmatched);
    }
  }, [data, nonAdminUsers, selectedProject, edit]);

  useEffect(() => {
    console.log("🟢 useEffect triggered");
    ReloadData(true);
  }, []);

  return (
    <div className="border rounded-lg bg-white font-GoogleSans tracking-wider overflow-hidden">
      <ConfirmationPopup
        isOpenConfirmPopup={isOpenConfirmPopup}
        confirmationMsg={confirmationMsg}
        closeConfirmPopup={closeConfirmPopup}
        onStateChange={handleDeleteState}
      />
      <Popup
        isOpen={isOpenPopup}
        msg={msg}
        closeModal={closePopup}
        isSuccess={isSuccess}
      />
      {/* ✅ Submitting overlay loader */}
      {isSubmitting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg px-8 py-6 flex flex-col items-center gap-3 shadow-xl">
            <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            <p className="text-sm font-medium text-gray-700">Assigning user(s), please wait...</p>
          </div>
        </div>
      )}
      <InputModal 
        overflowVisible={true} 
        isOpen={isOpen} 
        closeModal={closeModal} 
        title={edit === false ? 'Assign project users' : 'Reassign project users'} 
        savefn={edit === false ? handleCreateState : handleEditState}
        modalInputs={
          <div className='space-y-4'>
            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">Project</label>
              <SearchableSingleSelector
                items={projects}
                selected={selectedProject}
                setSelected={setSelectedProject}
                filterby="name"
                additionalshowprop="project_code"
                nooverlap={true}
                selectorIndex="name"
                noDataFoundMessage="No Projects found"
              />
            </div>

            {/* {edit === false && (
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={!showCreateUser}
                    onChange={() => setShowCreateUser(false)}
                    
                    className="mr-2"
                  />
                  Assign Existing Users
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={showCreateUser}
                    onChange={() => setShowCreateUser(true)}
                    className="mr-2"
                  />
                  Create New User
                </label>
              </div>
            )} */}

            {edit === false && (
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    id="assign-existing"
                    name="user-option"
                    checked={!showCreateUser}
                    onChange={() => {
                      setShowCreateUser(false);
                      setNewUserData({ name: '', email: '', password: '' });
                      setValidationErrors({});
                    }}
                    className="mr-2"
                  />
                  Assign Existing Users
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    id="create-new"
                    name="user-option"
                    checked={showCreateUser}
                    onChange={() => {
                      setShowCreateUser(true);
                      // setSelectedUser([]);
                      setNewUserData({ name: '', email: '', password: '' });
                      setValidationErrors({});
                    }}
                    className="mr-2"
                  />
                  Create New User
                </label>
              </div>
            )}

            {!showCreateUser ? (
              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">Users</label>
                {edit === true ? (
                  <SearchableSingleSelector
                    items={nonAdminUsers}
                    selected={selectedUser}
                    setSelected={setSelectedUser}
                    filterby="name"
                    additionalshowprop="user_id"
                    nooverlap={true}
                    selectorIndex="name"
                    noDataFoundMessage="No Users found"
                  />
                ) : (
                  <MultiInputSelector
                    items={unmatchedUsers}
                    selected={selectedUser}
                    setSelected={setSelectedUser}
                    selectorIndex="name"
                    filterby="user_id"
                  />
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-900">Name *</label>
                  <input
                    type="text"
                    autoComplete="off"
                    className={`block w-full rounded-md border py-1.5 mt-1 px-3 ${validationErrors.name ? 'border-red-500' : 'border-gray-300'}`}
                    value={newUserData.name}
                    onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
                  />
                  {validationErrors.name && <p className="text-red-500 text-sm mt-1">{validationErrors.name}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-900">Email *</label>
                  <input
                    type="email"
                    autoComplete="off"
                    className={`block w-full rounded-md border py-1.5 mt-1 px-3 ${validationErrors.email ? 'border-red-500' : 'border-gray-300'}`}
                    value={newUserData.email}
                    onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                  />
                  {validationErrors.email && <p className="text-red-500 text-sm mt-1">{validationErrors.email}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-900">Password *</label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    className={`block w-full rounded-md border py-1.5 mt-1 px-3 ${validationErrors.password ? 'border-red-500' : 'border-gray-300'}`}
                    value={newUserData.password}
                    onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                  />
                  {validationErrors.password && <p className="text-red-500 text-sm mt-1">{validationErrors.password}</p>}
                </div>
              </div>
            )}
          </div>
        }
      />

      <InputModal isOpen={isOpenStatusModal} closeModal={closeStatusModal} title={modalTitle} savefn={handleStatusState}
        modalInputs={
          <div className='flex gap-5'>
            <div className="flex items-center gap-x-2">
              <input
                id="status"
                name="status"
                type="radio"
                value="true"
                checked={rowData.status === true || rowData.status === "true"}
                onChange={(e) =>
                  setRowData({
                    ...rowData,
                    status: e.target.value,
                  })
                }
                autoFocus={false}
                className="h-4 w-4 border-gray-300 text-indigo-600 focus:outline-none focus:ring-0 focus:border-none"
              />
              <label htmlFor="push-everything" className="block text-sm font-medium leading-6 text-gray-900">
                Active
              </label>
            </div>
            <div className="flex items-center gap-x-2">
              <input
                id="status"
                name="status"
                type="radio"
                value="false"
                checked={rowData.status === false || rowData.status === "false"}
                onChange={(e) =>
                  setRowData({
                    ...rowData,
                    status: e.target.value,
                  })
                }
                autoFocus={false}
                className="h-4 w-4 border-gray-300 text-indigo-600 focus:outline-none focus:ring-0 focus:border-none"
              />
              <label htmlFor="push-email" className="block text-sm font-medium leading-6 text-gray-900">
                Inactive
              </label>
            </div>
          </div>
        }
      />
      <div className='flex justify-between m-3 gap-2'>
        <div>
          <DebouncedInput
            value={globalFilter ?? ''}
            onChange={value => setGlobalFilter(String(value))}
            placeholder="Search..."
          />
        </div>
        <div className='flex gap-2'>
          <button className="rounded-md cursor-default lg:cursor-pointer p-2 inline-flex text-sm border-gray-300 border" onClick={() => ReloadData(true)}>
            <ArrowPathIcon className='h-5 text-green-700' />
          </button>
          <Menu as="div" className="relative">
            <div>
              <Menu.Button className="relative justify-center border border-gray-300 rounded-md p-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 cursor-default lg:cursor-pointer">
                <span className="absolute -inset-1.5" />
                <FunnelIcon className="h-5 w-auto" />
              </Menu.Button>
            </div>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="fixed right-8 z-30 mt-3 w-fit px-2 pb-2 space-y-3 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div>
                  {data.length > 0 &&
                    table
                      .getHeaderGroups()
                      .map((headerGroup) => (
                        <div
                          key={headerGroup.id}
                          className="space-y-1"
                        >
                          {headerGroup.headers.map(
                            (header) => (
                              <div
                                key={header.id}
                              >
                                {header.column.getCanFilter() &&
                                  header.column.id === "status" ? (
                                  <div>
                                    <label
                                      htmlFor="push-everything"
                                      className="block text-xs font-bold tracking-widest leading-6 text-gray-400 ps-1"
                                    >
                                      Status
                                    </label>
                                    <StatusAdminFilter
                                      column={
                                        header.column
                                      }
                                      table={
                                        table
                                      }
                                    />
                                  </div>
                                ) : null}
                                {header.column.getCanFilter() &&
                                  header.column.id === "project_name" ? (
                                  <div>
                                    <label
                                      htmlFor="push-everything"
                                      className="block text-xs font-bold tracking-widest leading-6 text-gray-400 ps-1"
                                    >
                                      Project
                                    </label>
                                    <SelectTextFilterWithAllOption
                                      column={
                                        header.column
                                      }
                                      table={
                                        table
                                      }
                                    />
                                  </div>
                                ) : null}
                                {header.column.getCanFilter() &&
                                  header.column.id === "user_name" ? (
                                  <div>
                                    <label
                                      htmlFor="push-everything"
                                      className="block text-xs font-bold tracking-widest leading-6 text-gray-400 ps-1"
                                    >
                                      User
                                    </label>
                                    <SelectTextFilterWithAllOption
                                      column={
                                        header.column
                                      }
                                      table={
                                        table
                                      }
                                    />
                                  </div>
                                ) : null}
                              </div>
                            )
                          )}
                        </div>
                      ))}
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
          <button className="rounded-md cursor-default lg:cursor-pointer p-2 inline-flex text-sm border-gray-300 border" onClick={() => openModal('e', 'create')}>
            <PlusCircleIcon className='h-5' />
          </button>
        </div>
      </div>
      {Loader && <FetchingDataLoader />}
      {!Loader &&
        <>
          <div ref={tableContainerRef} className='resp-table-h border-y overflow-auto relative'>
            <table className='flex-inline w-full border-collapse'>
              <thead className='shadow bg-gray-100'>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <th
                          className='border p-2 whitespace-nowrap'
                          key={header.id}
                          colSpan={header.colSpan}
                          style={{ width: header.getSize() }}
                        >
                          {header.isPlaceholder ? null : (
                            <>
                              <div
                                className={`${header.column.getCanSort()
                                  ? 'cursor-pointer select-none justify-start'
                                  : 'justify-center'
                                  } flex`}
                                onClick={header.column.getToggleSortingHandler()}
                              >
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                                {{
                                  asc: ' 🔼',
                                  desc: ' 🔽',
                                }[header.column.getIsSorted()] || null}
                              </div>
                            </>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {
                  rows.length === 0 ?
                    (
                      <tr>
                        <td className='border p-2' colSpan={table.getHeaderGroups()[0].headers.length} style={{ textAlign: 'center' }}>
                          No Data Found
                        </td>
                      </tr>
                    )
                    :
                    (
                      <>
                        {paddingTop > 0 && (
                          <tr>
                            <td className='border p-2 whitespace-nowrap' style={{ height: `${paddingTop}px` }} />
                          </tr>
                        )}
                        {virtualRows.map((virtualRow) => {
                          const row = rows[virtualRow.index];
                          return (
                            <tr key={row.id}>
                              {row.getVisibleCells().map((cell) => {
                                return (
                                  <td className='border p-2 whitespace-nowrap' key={cell.id}>
                                    {cell.column.id === "index" && (
                                      <center>{renderTableCellIndex(row)}</center>
                                    )}
                                    {cell.column.id !== "index" &&
                                      flexRender(
                                        cell.column
                                          .columnDef
                                          .cell,
                                        cell.getContext()
                                      )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                        {paddingBottom > 0 && (
                          <tr>
                            <td className='border p-2' style={{ height: `${paddingBottom}px` }} />
                          </tr>
                        )}
                      </>
                    )
                }
              </tbody>
            </table>
          </div>
          {
            rows.length > 0 && (
              <div className='flex gap-1 flex-wrap md:flex-nowrap justify-between flex-row-reverse m-3'>
                <Pagination
                  pageIndex={pageIndex}
                  setPageIndex={setPageIndex}
                  pageNumbers={pageNumbers}
                  pageCount={table.getPageCount()}
                />
                <PageSizeDropdown
                  dataLength={table.getPrePaginationRowModel().rows.length}
                  pageSize={pageSize}
                  handlePageChange={handlePageChange}
                  totalRows={table.getPrePaginationRowModel().rows.length}
                />
              </div>
            )
          }
        </>
      }
    </div>
  );
}